/**
 * `Corbeille` — 30-day trash bin for soft-deleted candidates.
 *
 * A candidate lands here when an admin clicks "Envoyer à la corbeille"
 * (default `DELETE /api/candidates/{id}` behaviour). The row displays
 * the days remaining before automatic purge, who trashed it, and two
 * actions:
 *   1. Restore — moves the candidate back to `candidates` collection,
 *      Kanban card reappears exactly where it was.
 *   2. Delete permanently — irreversible, requires typing the first
 *      name to confirm (safety gate).
 *
 * A daily scheduler (`services/trash_scheduler.py`) hard-deletes rows
 * whose `purge_scheduled_at` is in the past — no admin action needed
 * to keep the DB clean.
 */
import { AlertTriangle, ArrowUp, Clock, Loader2, RotateCcw, Search, Trash2, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";


function daysUntilPurge(purgeIso) {
    if (!purgeIso) return null;
    const now = Date.now();
    const purge = new Date(purgeIso).getTime();
    return Math.max(0, Math.round((purge - now) / (24 * 3600 * 1000)));
}


export default function Corbeille() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(null); // { id, action: "restore" | "purge" }
    const [purgeConfirm, setPurgeConfirm] = useState({ open: false, row: null, typed: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/candidates/trash/list");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de charger la corbeille");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((r) => {
            const hay = `${r.first_name || ""} ${r.last_name || ""} ${r.email || ""} ${r.deleted_by_name || ""}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [rows, q]);

    async function handleRestore(row) {
        setBusy({ id: row.id, action: "restore" });
        try {
            await api.post(`/candidates/${row.id}/restore`);
            toast.success(`${row.first_name} ${row.last_name} restauré`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la restauration");
        } finally {
            setBusy(null);
        }
    }

    async function handlePurge() {
        const row = purgeConfirm.row;
        if (!row) return;
        setBusy({ id: row.id, action: "purge" });
        try {
            try {
                await api.delete(`/candidates/trash/${row.id}`);
            } catch (err) {
                if (err.response?.status === 409) {
                    const ok = window.confirm(
                        (err.response.data?.detail || "") +
                        "\n\nContinuer et supprimer QUAND MÊME ? Le contrat signé sera conservé.",
                    );
                    if (!ok) throw err;
                    await api.delete(`/candidates/trash/${row.id}?force=true`);
                } else {
                    throw err;
                }
            }
            toast.success(`${row.first_name} ${row.last_name} définitivement supprimé`);
            setPurgeConfirm({ open: false, row: null, typed: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
        } finally {
            setBusy(null);
        }
    }

    const purgeAllowed = purgeConfirm.row
        ? purgeConfirm.typed.trim().toLowerCase() === (purgeConfirm.row.first_name || "").trim().toLowerCase()
        : false;

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="corbeille-page">
            <header className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-red-100 text-red-700 inline-flex items-center justify-center">
                        <Trash2 className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-shariti-black">Corbeille</h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Candidats supprimés · récupérables 30 jours · purge automatique au-delà
                        </p>
                    </div>
                </div>
            </header>

            <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher par nom, email, admin…"
                        data-testid="corbeille-search"
                        className="w-full rounded-full border-2 border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-shariti-orange focus:outline-none"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="corbeille-refresh"
                    className="rounded-full border-2 border-slate-200 hover:border-shariti-orange px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-600 hover:text-shariti-orange transition"
                >
                    Actualiser
                </button>
                <div className="text-[11px] text-slate-500 ml-auto">
                    {loading ? "Chargement…" : `${filtered.length} candidat${filtered.length > 1 ? "s" : ""} en corbeille`}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 p-12 text-center">
                    <Trash2 className="h-8 w-8 mx-auto text-slate-300" />
                    <p className="text-sm text-slate-500 mt-2">
                        {rows.length === 0 ? "Aucun candidat dans la corbeille." : "Aucun résultat pour cette recherche."}
                    </p>
                </div>
            ) : (
                <div className="rounded-2xl border-2 border-slate-100 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">Candidat</th>
                                <th className="text-left px-3 py-2">Supprimé le</th>
                                <th className="text-left px-3 py-2">Par</th>
                                <th className="text-left px-3 py-2">Purge dans</th>
                                <th className="text-right px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((r) => {
                                const days = daysUntilPurge(r.purge_scheduled_at);
                                const urgent = days !== null && days <= 3;
                                return (
                                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`corbeille-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-black text-shariti-black">
                                                {r.first_name} {r.last_name}
                                            </div>
                                            <div className="text-xs text-slate-500">{r.email || "—"}</div>
                                            {r.position && r.position !== "—" && (
                                                <div className="text-[10px] text-slate-400 mt-0.5">{r.position}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-slate-600 text-xs whitespace-nowrap">
                                            {(r.deleted_at || "").slice(0, 16).replace("T", " ")}
                                        </td>
                                        <td className="px-3 py-2 text-slate-600 text-xs">
                                            {r.deleted_by_name || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                    urgent
                                                        ? "bg-red-100 text-red-800 border border-red-200"
                                                        : "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                <Clock className="h-3 w-3" />
                                                {days !== null ? `${days} jours` : "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRestore(r)}
                                                    disabled={busy?.id === r.id}
                                                    data-testid={`corbeille-restore-${r.id}`}
                                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-[11px] font-black uppercase tracking-wider"
                                                    title="Restaurer ce candidat"
                                                >
                                                    {busy?.id === r.id && busy?.action === "restore" ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <RotateCcw className="h-3 w-3" />
                                                    )}
                                                    Restaurer
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPurgeConfirm({ open: true, row: r, typed: "" })}
                                                    disabled={busy?.id === r.id}
                                                    data-testid={`corbeille-purge-${r.id}`}
                                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60 text-[11px] font-black uppercase tracking-wider"
                                                    title="Supprimer définitivement (irréversible)"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                    Supprimer
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {purgeConfirm.open && purgeConfirm.row && (
                <div
                    className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
                    onClick={() => busy?.action !== "purge" && setPurgeConfirm({ open: false, row: null, typed: "" })}
                    data-testid="corbeille-purge-modal"
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 bg-red-50 border-b-2 border-red-200">
                            <div className="flex items-center gap-2 text-red-800">
                                <TriangleAlert className="h-5 w-5" />
                                <div className="text-sm font-black uppercase tracking-widest">
                                    Suppression définitive
                                </div>
                            </div>
                            <p className="text-xs text-red-900/80 mt-2 leading-relaxed">
                                Cette action est <strong>irréversible</strong>. Toutes les données de <strong>{purgeConfirm.row.first_name} {purgeConfirm.row.last_name}</strong> (fiche, notifs, invitations Academy, scorecards) seront perdues.
                                Les contrats signés seront conservés (obligation légale).
                            </p>
                        </div>
                        <div className="p-5 space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                                Tape « {purgeConfirm.row.first_name} » pour confirmer
                            </label>
                            <input
                                type="text"
                                value={purgeConfirm.typed}
                                onChange={(e) => setPurgeConfirm((p) => ({ ...p, typed: e.target.value }))}
                                placeholder={purgeConfirm.row.first_name}
                                autoFocus
                                autoComplete="off"
                                data-testid="corbeille-purge-confirm-name"
                                className="w-full rounded-lg border-2 border-red-300 bg-white px-3 py-2 text-sm focus:border-red-600 focus:outline-none"
                            />
                        </div>
                        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setPurgeConfirm({ open: false, row: null, typed: "" })}
                                disabled={busy?.action === "purge"}
                                className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={handlePurge}
                                disabled={!purgeAllowed || busy?.action === "purge"}
                                data-testid="corbeille-purge-confirm"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest bg-red-700 text-white hover:bg-red-800 disabled:opacity-60"
                            >
                                {busy?.action === "purge" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                {busy?.action === "purge" ? "Suppression…" : "Supprimer définitivement"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
